<script>
import Layout from '@layouts/adminLayout.vue'
import LoaderIcon from '@components/loader-icon.vue'
import { searchResources } from '@src/api/resources'
import { transformMany as transformResources } from '@src/api/transformers/resourceTransformer'
import ResourceBlock from '@components/resources/resource-block.vue'
import ModalResource from '@components/resources/modal-resource.vue'

export default {
  page: {
    title: 'Search',
    meta: [
      {
        name: 'description',
        content: 'The Search page.',
      },
    ],
  },
  components: {
    Layout,
    LoaderIcon,
    ResourceBlock,
    ModalResource,
  },
  data() {
    return {
      loading: true,
      error: false,
      searchQuery: '',
      searchCategory: '',
      resources: [],
      resultsPerPage: 12,
    }
  },
  computed: {
    getSearchResources() {
      return this.resources.slice(0, this.resultsPerPage)
    },
    isShowMoreButtonVisible() {
      return this.resources.length > this.resultsPerPage
    },
  },
  mounted() {
    const { query, category } = this.$route.query
    this.searchQuery = query
    this.searchCategory = category
    this.search(query, category)
  },
  methods: {
    async search(searchQuery, searchCategory) {
      if (!searchQuery) {
        await this.$router.push({ name: 'resources' })
        return
      }

      try {
        this.loading = true
        const {
          data: { data: searchResult },
        } = await searchResources({
          query: searchQuery,
          categoryName: searchCategory,
        })
        this.resources = transformResources(searchResult)
        this.$store.commit('resources/SET_RESOURCES', transformResources(searchResult))
        this.$store.commit('resources/SET_SHOULD_RELOAD_RESOURCES', true)
      } catch (err) {
        this.resources = []
      } finally {
        this.loading = false
      }
    },
    showMoreResults() {
      this.resultsPerPage += 12
    },
  },
}
</script>

<template>
  <Layout>
    <LoaderIcon v-if="loading || error" :error="error" />
    <div v-else>
      <BaseTitle>Resources</BaseTitle>
      <div class="d-flex">
        <h4 v-if="resources.length" class="color-dark-gray font-weight-bold mx-auto">
          {{ resources.length }} results for
          <span class="color-primary-blue">"{{ searchQuery.trim() }}"</span>
          <template v-if="searchCategory">
            in
            <span class="color-primary-blue">{{ _.startCase(searchCategory) }}</span>
          </template>
        </h4>
        <h4 v-if="!resources.length" class="color-dark-gray font-weight-bold mx-auto">
          No results for
          <span class="color-primary-blue">"{{ searchQuery.trim() }}"</span>
          <template v-if="searchCategory">
            in
            <span class="color-primary-blue">{{ _.startCase(searchCategory) }}</span>
          </template>
        </h4>
      </div>
      <div :class="$style.gridView">
        <ResourceBlock
          v-for="resource in getSearchResources"
          :key="resource.resourceId"
          :class="$style.resourceBlock"
          :resource="resource" />
      </div>
      <BaseButton v-if="isShowMoreButtonVisible" :class="$style.showMoreButton" @click="showMoreResults">
        <span>Show More</span>
      </BaseButton>
    </div>

    <b-modal
      id="view-resource-modal"
      :modal-class="['right', 'modal-resource']"
      hide-footer
      hide-header
      title="BootstrapVue">
      <ModalResource :toast-component="this.$bvToast" />
    </b-modal>
  </Layout>
</template>

<style lang="scss" module>
@import '@design';

.showMoreButton {
  display: block;
  width: 193px;
  margin: 18px auto;
}

.gridView {
  @media (max-width: $lg-breakpoint) {
    grid-template-columns: repeat(auto-fill, minmax(327px, 1fr));
    grid-gap: 16px 16px;
  }

  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(275px, 1fr));
  grid-gap: 32px 24px;
  margin-top: 24px;
}

.resourceBlock {
  @media (max-width: $lg-breakpoint) {
    width: 100%;
  }

  min-width: 275px;
}
</style>
